import React from 'react';

interface TitleCardPreviewProps {
  text?: string | null;
  fontSize?: number;
  backgroundColor: string;
  logoUrl?: string | null;
  logoScale?: number;
  tagline?: string | null;
  animation?: string;
  label: string;
  aspectRatio?: '16:9' | '9:16' | '1:1';
}

export default function TitleCardPreview({
  text,
  fontSize = 64,
  backgroundColor,
  logoUrl,
  logoScale = 0.15,
  tagline,
  animation,
  label,
  aspectRatio = '16:9',
}: TitleCardPreviewProps) {
  const REFERENCE_WIDTH = 1920;
  const scaledFont = `${(fontSize / REFERENCE_WIDTH) * 100}cqw`;
  const logoWidthPercent = Math.round(logoScale * 100);
  const isLogoOnly = logoUrl && !text;

  const animationClass =
    animation === 'zoomIn'
      ? 'animate-[scaleIn_0.8s_ease-out_forwards]'
      : animation === 'slideUp'
        ? 'animate-[slideUp_0.6s_ease-out_forwards]'
        : animation === 'fadeIn'
          ? 'animate-[fadeIn_0.6s_ease-out_forwards]'
          : '';

  const isPortrait = aspectRatio === '9:16';
  const isSquare = aspectRatio === '1:1';
  const cssAspect = isPortrait ? '9 / 16' : isSquare ? '1 / 1' : '16 / 9';
  const ratioLabel: '9:16' | '16:9' | '1:1' = isPortrait ? '9:16' : isSquare ? '1:1' : '16:9';

  return (
    <div className="mt-3">
      <span className="text-[10px] text-slate-600 uppercase tracking-wider">{label} Preview</span>
      <div
        className={`relative mt-1 rounded-lg border border-slate-700 overflow-hidden ${
          isPortrait ? 'w-1/2 mx-auto' : 'w-full'
        }`}
        style={{
          aspectRatio: cssAspect,
          backgroundColor,
          containerType: 'inline-size',
        }}
      >
        <div className={`absolute inset-0 flex flex-col items-center justify-center px-6 gap-3 ${animationClass}`}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Logo"
              className="object-contain"
              style={{
                width: isLogoOnly ? `${Math.min(logoWidthPercent * 2, 60)}%` : `${logoWidthPercent}%`,
                maxHeight: isLogoOnly ? '45%' : '30%',
                opacity: 0.9,
              }}
            />
          )}
          {text && (
            <span
              className="text-white text-center leading-tight font-light"
              style={{
                fontSize: scaledFont,
                textShadow: '0 2px 12px rgba(0,0,0,0.5)',
                maxWidth: '85%',
                wordBreak: 'break-word',
              }}
            >
              {text}
            </span>
          )}
          {tagline && isLogoOnly && (
            <span
              className="text-white/60 text-center font-light"
              style={{
                fontSize: `${(28 / REFERENCE_WIDTH) * 100}cqw`,
                maxWidth: '70%',
                letterSpacing: '0.05em',
              }}
            >
              {tagline}
            </span>
          )}
        </div>

        <span className="absolute bottom-1.5 right-2 text-[9px] text-white/20 font-mono">
          {ratioLabel}
        </span>
      </div>
    </div>
  );
}
